import React, { useState } from 'react'
import { createClient } from '@supabase/supabase-js'
import { sendSecurityAlert } from '../services/api'

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_KEY
)

const COLORS = {
  primary: '#d4602a',
  secondary: '#3d7a5e',
  bg: '#2d2d2d',
  card: '#f5f2ec',
  border: '#ddd9d0'
}

export default function Login({ onLogin }) {
  const [view, setView] = useState('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [totpCode, setTotpCode] = useState('')
  const [qrCode, setQrCode] = useState('')
  const [factorId, setFactorId] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  async function handleLogin() {
    setError('')
    setLoading(true)
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({ email, password })
      if (signInError) throw new Error(signInError.message)

      // Verificar que es super admin (ahora autenticado)
      const { data: adminCheck } = await supabase
        .from('super_admins')
        .select('email')
        .eq('email', email)
        .eq('is_active', true)
        .single()

      if (!adminCheck) {
        await supabase.auth.signOut()
        throw new Error('Access denied. You are not authorized.')
      }

      // Verificar si tiene MFA configurado
      const { data: factorsData } = await supabase.auth.mfa.listFactors()
      const totpFactor = factorsData?.totp?.[0]

      if (!totpFactor) {
        const { data: enrollData, error: enrollError } = await supabase.auth.mfa.enroll({ factorType: 'totp', issuer: 'PYRO Admin' })
        if (enrollError) throw new Error(enrollError.message)
        setQrCode(enrollData.totp.qr_code)
        setFactorId(enrollData.id)
        setView('setup-mfa')
      } else {
        setFactorId(totpFactor.id)
        setView('verify-mfa')
      }
    } catch (e) {
      setError(e.message)
    }
    setLoading(false)
  }

  async function handleVerifyMFA() {
    setError('')
    setLoading(true)
    try {
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({ factorId })
      if (challengeError) throw new Error(challengeError.message)

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code: totpCode
      })
      if (verifyError) throw new Error('Invalid code. Please try again.')

      await sendSecurityAlert('super_admin_login', email, { timestamp: new Date().toISOString() })
      onLogin()
    } catch (e) {
      setError(e.message)
    }
    setLoading(false)
  }

  const styles = {
    body: { fontFamily: "'Segoe UI', sans-serif", background: COLORS.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' },
    box: { background: COLORS.card, borderRadius: 16, padding: 40, width: 400, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
    label: { display: 'block', fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 6 },
    input: { width: '100%', padding: '10px 14px', border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' },
    btn: { width: '100%', padding: 12, background: COLORS.primary, color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: 'pointer', marginTop: 8 },
    error: { background: '#fee2e2', color: '#dc2626', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16 },
    divider: { height: 3, background: `linear-gradient(to right, ${COLORS.secondary}, ${COLORS.primary}, #c9a0dc, ${COLORS.secondary})`, borderRadius: 2, marginBottom: 28 }
  }

  return (
    <div style={styles.body}>
      <div style={styles.box}>
        <div style={{ textAlign: 'center', fontSize: 28, fontWeight: 700, color: '#2d2d2d', marginBottom: 4 }}>
          PYRO <span style={{ color: COLORS.primary }}>ADMIN</span>
        </div>
        <div style={{ textAlign: 'center', color: '#888', fontSize: 13, marginBottom: 16 }}>
          {view === 'login' && 'Administration Panel'}
          {view === 'setup-mfa' && 'Set up Two-Factor Authentication'}
          {view === 'verify-mfa' && 'Enter Authentication Code'}
        </div>
        <div style={styles.divider} />

        {error && <div style={styles.error}>{error}</div>}

        {view === 'login' && (
          <>
            <div style={{ marginBottom: 16 }}>
              <label style={styles.label}>Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleLogin()} placeholder="admin@pyrobpo.com" style={styles.input} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={styles.label}>Password</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleLogin()} placeholder="••••••••" style={styles.input} />
            </div>
            <button onClick={handleLogin} disabled={loading} style={styles.btn}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </>
        )}

        {view === 'setup-mfa' && (
          <>
            <p style={{ fontSize: 13, color: '#555', marginBottom: 16 }}>
              Scan this QR code with Google Authenticator or Authy, then enter the 6-digit code to confirm setup.
            </p>
            {qrCode && (
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <img src={qrCode} alt="QR Code" style={{ width: 200, height: 200, border: `1px solid ${COLORS.border}`, borderRadius: 8 }} />
              </div>
            )}
            <div style={{ marginBottom: 20 }}>
              <label style={styles.label}>Verification Code</label>
              <input
                type="text"
                value={totpCode}
                onChange={e => setTotpCode(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleVerifyMFA()}
                placeholder="000000"
                maxLength={6}
                style={{ ...styles.input, letterSpacing: 8, fontSize: 20, textAlign: 'center' }}
              />
            </div>
            <button onClick={handleVerifyMFA} disabled={loading} style={styles.btn}>
              {loading ? 'Verifying...' : 'Confirm Setup'}
            </button>
          </>
        )}

        {view === 'verify-mfa' && (
          <>
            <p style={{ fontSize: 13, color: '#555', marginBottom: 16 }}>
              Open your authenticator app and enter the 6-digit code.
            </p>
            <div style={{ marginBottom: 20 }}>
              <label style={styles.label}>Authentication Code</label>
              <input
                type="text"
                value={totpCode}
                onChange={e => setTotpCode(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleVerifyMFA()}
                placeholder="000000"
                maxLength={6}
                style={{ ...styles.input, letterSpacing: 8, fontSize: 20, textAlign: 'center' }}
              />
            </div>
            <button onClick={handleVerifyMFA} disabled={loading} style={styles.btn}>
              {loading ? 'Verifying...' : 'Verify'}
            </button>
          </>
        )}
      </div>
    </div>
  )
}